<template>
  <!-- Map 上の Popup -->
  <div
    ref="popup"
    class="popup-box"
    v-if="popup"
    v-bind:style="{ transform: `translate(${popup.x}px, ${popup.y}px)` }"
  >
    <!-- Popup コンテンツ -->
    <map-popup v-on:resize="$_onResize" v-on:close="$_onClose">
      <template v-slot:header>
        <h5>
          {{ popup && popup.label }}
        </h5>
      </template>
      <template v-slot:body>
        <loading-mask v-bind:is-loading="isLoading">
          <div class="body">
            <div class="item-icon item-icon-user">
              <user-icon class="svg-icon" />
            </div>
            <div class="item-text item-text-user">
              {{ $t('appMapPopupIndoorBuilding.count', { n: count.member }) }}
            </div>
            <div />
            <div class="item-icon item-icon-place">
              <place-icon class="svg-icon" />
            </div>
            <div class="item-text item-text-place">
              {{ $t('appMapPopupIndoorBuilding.count', { n: count.place }) }}
            </div>
            <div />
          </div>
        </loading-mask>
      </template>
      <template v-slot:footer>
        <div class="footer">
          <div
            class="link"
            v-on:click="$_onClickBuildingDetail"
            v-t="'appMapPopupIndoorBuilding.buildingInformation'"
          ></div>
          <div
            class="link"
            v-on:click="$_onClickToBuilding"
            v-t="'appMapPopupIndoorBuilding.toIndoor'"
          ></div>
        </div>
      </template>
    </map-popup>
  </div>
</template>

<script>
import AppMapPopup from '@/components/map/AppMapPopup.vue'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { mapState, mapGetters } from 'vuex'
import { RestApiClient } from 'ifield-core-components'
import PlaceIcon from '@/assets/ifield/ic_place.svg?component'
import UserIcon from '@/assets/ifield/ic_user.svg?component'
import { MAP_LAYER_REFERENCE_TABLE_TYPE } from '@/constants.js'

const LOADING_FAIL = '--' // ローディング失敗の文字列
const DEFAULT_MIN_ZOOM = 15 // 表示する最小ズームのデフォルト値
const DEFAULT_MAX_ZOOM = 22 // 表示する最大ズームのデフォルト値

/**
 * pathからスケールを取得する
 * @param {String} path パス
 * @returns {Number} スケール
 */
const getScaleFromPath = (path) => {
  const scaleIndex = path.search(/@[1-9]x/)
  if (scaleIndex === -1) {
    return 1
  } else {
    return +path[scaleIndex + 1]
  }
}

/**
 * Map 上の 屋内建物 Popup コンテンツ (AppMapControl と同じ <div> 内に入れる)
 */
export default {
  name: 'AppMapPopupIndoorBuilding',
  components: {
    'map-popup': AppMapPopup,
    'place-icon': PlaceIcon,
    'user-icon': UserIcon,
  },
  props: {
    /**
     * マップコンポーネント
     */
    currentMapComponent: {
      type: Object,
      default: null,
    },
    /**
     * マップのClientRect
     */
    mapClientRect: {
      type: [Object, DOMRect],
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      indoorBuildingId: null,
      members: [],
      placeCount: 0,
      popup: null,
      popupData: null,
      clientRect: null,
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters('map', ['center', 'zoom', 'heading']),
    ...mapGetters('mapLayer', [
      'clickedIndoorBuilding',
      'mapLayers',
      'markerImageStore',
    ]),
    ...mapState('indoorBuilding', ['indoorBuildings']),
    /**
     * 対象の屋内建物データ
     * @type {Object}
     */
    indoorBuilding: function () {
      return (
        this.indoorBuildings.find((b) => b.id === this.indoorBuildingId) || null
      )
    },
    /**
     * 屋内建物にいる人数 （建物内の屋内レベルいる人数)
     * @type {Number}
     */
    memberCount: function () {
      if (!this.members) {
        return null
      }
      let count = 0
      this.members.forEach((v) => {
        if (this.indoorLevelIds.includes(v.indoor_level_id)) {
          count++
        }
      })
      return count
    },
    /**
     * 件数表示
     * @type {Object}
     */
    count: function () {
      return {
        member: this.memberCount == null ? LOADING_FAIL : this.memberCount,
        place: this.placeCount == null ? LOADING_FAIL : this.placeCount,
      }
    },
    /**
     * 該当建物の屋内レベルID一覧
     * @type {Array.<Number>}
     */
    indoorLevelIds: function () {
      if (!this.indoorBuilding) {
        return []
      }
      return this.indoorBuilding.indoor_levels.map((v) => v.id)
    },
    /**
     * ビルのマップレイヤー
     * @type {Object}
     */
    layer: function () {
      return (
        this.mapLayers.find(
          (v) =>
            v.reference_table_type ===
            MAP_LAYER_REFERENCE_TABLE_TYPE.INDOOR_BUILDING
        ) || null
      )
    },
    /**
     * マーカーのMapping
     * @type {Object}
     */
    markerMapping: function () {
      if (!this.layer || !this.markerImageStore.size) return null
      const marker = this.markerImageStore.get(this.layer.marker_path)
      return (marker && marker.originalMapping) || null
    },
    /**
     * マーカーのスケール
     * @type {Number}
     */
    markerScale: function () {
      if (!this.layer) return 1
      return getScaleFromPath(this.layer.marker_path)
    },
    /**
     * ポップアップoffset
     * @type {Object}
     */
    popupOffset: function () {
      if (!this.clientRect && !this.markerMapping) {
        return { x: -100, y: -190 }
      }
      let [markerOffsetY, rectOffsetX, rectOffsetY] = [0, 0, 0]
      if (this.markerMapping) {
        const { height, anchorY } = this.markerMapping
        markerOffsetY = -height * anchorY
      }
      if (this.clientRect) {
        rectOffsetX = -this.clientRect.width / 2
        rectOffsetY = -this.clientRect.height
      }
      // FIXME: -10はbeakのサイズ 取得するべき
      return {
        x: rectOffsetX,
        y: markerOffsetY / this.markerScale + rectOffsetY - 10,
      }
    },
    /**
     * Popup 表示の最小ズーム
     * @type {Number}
     */
    minZoom() {
      return this.layer ? this.layer.min_zoom : DEFAULT_MIN_ZOOM
    },
    /**
     * Popup 表示の最大ズーム
     * @type {Number}
     */
    maxZoom() {
      return this.layer ? this.layer.max_zoom : DEFAULT_MAX_ZOOM
    },
  },
  watch: {
    clickedIndoorBuilding(value) {
      this.$_onClickedIndoorBuildingChange(value)
    },
    /**
     * [Watch] マップの中心
     */
    center() {
      this.$_onUpdateMapState()
    },
    /**
     * [Watch] マップのズーム
     */
    zoom() {
      this.$_onUpdateMapState()
    },
    /**
     * [Watch] マップの回転角度
     */
    heading() {
      this.$_onUpdateMapState()
    },
  },
  methods: {
    /**
     * クリック屋内建物変更時処理
     * @param {Object} value クリックされた建物情報
     */
    $_onClickedIndoorBuildingChange: function (value) {
      // Click された Feature が変更された
      // プレイス詳細ダイアログを表示する
      this.$log.debug('[clickedIndoorBuilding] watch', value)

      if (!value) {
        return this.$_hide()
      } else if (value.geometry.type !== 'Point') {
        // Polygon がクリックされたので、Popup 消去で何もしない
        return this.$_hide()
      }

      this.indoorBuildingId = value.properties.indoor_building_id
      if (
        this.popup &&
        this.popupData &&
        this.popupData.indoor_building_id === this.indoorBuildingId
      ) {
        // すでに表示済みかつ、建物に変更がない（同じ建物クリック）
        // トグルで消去する
        return this.$_hide()
      }

      if (this.indoorLevelIds.length === 0) {
        // 屋内レベルがないので、当然ゼロ件
        this.placeCount = 0
      } else {
        // データ取得
        this.$_fetchRemote()
      }

      // データを設定する
      const [lng, lat] = value.geometry.coordinates
      this.popupData = {
        lat,
        lng,
        center: this.center,
        indoor_building_id: this.indoorBuildingId,
        label: `${value.properties.label}`,
      }

      // 描画
      this.renderPopup()
    },

    /**
     * ポップアップを描画
     */
    renderPopup() {
      if (!this.popupData) {
        // データなし ⇒ 非表示にする
        this.popup = null
        return
      }
      if (this.zoom < this.minZoom || this.zoom > this.maxZoom) {
        // 表示ズーム範囲外なので非表示（屋内建物の表示と連動）
        this.popup = null
        return
      }

      // Popup の表示位置の取得
      if (!this.currentMapComponent) {
        this.$log.warn('[render] Popup required currentMapComponent is null')
        return
      }
      const point = this.currentMapComponent.fromLatlngtoPixel(
        this.popupData.lat,
        this.popupData.lng
      )

      const value = {
        x: point.x + this.popupOffset.x,
        y: point.y + this.popupOffset.y,
        label: this.popupData.label,
        indoor_building_id: this.popupData.indoor_building_id,
      }

      // 表示可能領域か検査
      if (!this.mapClientRect) {
        this.$log.warn('[render] Popup required mapClientRect is null')
        return
      }
      if (
        point.x < this.popupOffset.x ||
        point.y < 0 ||
        point.x > this.mapClientRect.width - this.popupOffset.x ||
        point.y > this.mapClientRect.height - this.popupOffset.y
      ) {
        this.popup = null
        return
      }

      if (!this.popup || this.popup.x !== value.x || this.popup.y !== value.y) {
        // 変更がある時のみ
        this.popup = value
      }
    },

    /**
     * リモートから API で表示するデータを取得する
     */
    $_fetchRemote: async function () {
      this.$_showLoading()
      await Promise.all([this.$_fetchMember(), this.$_fetchPlaceCount()])
      this.$_hideLoading()
    },

    /**
     * リモートから API で表示するデータを取得する(member)
     */
    $_fetchMember: async function () {
      // リモートから メンバーの一覧取得
      // !FIXME: 理想的には API で絞り込みたい
      const url = RestApiClient.createApiUrl('user/status')
      try {
        const json = await RestApiClient.executeGetApi(url)
        // 人数のカウントに使用するだけなのでタイムゾーン変換していない
        this.members = json.data
      } catch (error) {
        // エラーが発生した場合はデータを削除する
        this.members = null
        this.handleApiError(error, null)
      }
    },

    /**
     * リモートから API で表示するデータを取得する(Place)
     */
    $_fetchPlaceCount: async function () {
      const url = RestApiClient.createApiUrl('place/count')
      url.searchParams.append('indoor_level_id', this.indoorLevelIds.join(','))

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.placeCount = json.data.count
      } catch (error) {
        // エラーが発生した場合はデータを削除する
        this.placeCount = null
        this.handleApiError(error, null)
      }
    },

    /**
     * Map の状態が更新された
     */
    $_onUpdateMapState() {
      if (this.zoom < this.minZoom || this.zoom > this.maxZoom) {
        // 表示ズーム範囲外になったらクローズしたことにする
        this.popupData = null
      }
      // this.popupData = null // (マップ移動時に表示する場合はここを設定)
      this.renderPopup()
    },

    /**
     * 建物詳細情報リンククリック時処理
     */
    $_onClickBuildingDetail: function () {
      if (!this.indoorBuilding) {
        // 権限がない
        this.$_showNotAllowedAlert()
        return
      }
      /**
       * 建物詳細情報リンククリックイベント
       * @param {Number} id 建物ID
       */
      this.$emit('map-popup-building-detail-click', this.indoorBuildingId)
    },

    /**
     * ポップアップ上の "建物内へ移動" クリック
     */
    $_onClickToBuilding() {
      this.$log.debug('$_onClickPopupToBuilding To => ', this.popupData)
      if (!this.popupData) {
        return
      }

      if (!this.indoorBuilding) {
        // 権限がない
        this.$_showNotAllowedAlert()
        return
      } else if (!this.indoorLevelIds.length) {
        // フロアがない
        this.$_showNoFloorAlert()
        return
      }

      // アクティブな indoor_building_id にする
      this.$store.commit(
        'mapIndoor/setActiveIndoorBuildingId',
        this.indoorBuildingId
      )

      // 非表示にする
      this.$_hide()
    },

    /**
     * 権限が無い場合のアラート表示
     */
    $_showNotAllowedAlert: function () {
      // 権限がない
      this.$alert.fire({
        icon: 'error',
        title: this.$t('appMapPopupIndoorBuilding.message.notAllow'),
        text: this.$t('appMapPopupIndoorBuilding.message.noPermission'),
      })
    },

    /**
     * フロアが無い場合のアラート表示
     */
    $_showNoFloorAlert: function () {
      this.$alert.fire({
        icon: 'error',
        title: this.$t('appMapPopupIndoorBuilding.message.notAllow'),
        text: this.$t('appMapPopupIndoorBuilding.message.noFloor'),
      })
    },

    /**
     * リサイズイベント
     */
    $_onResize: function (clientRect) {
      this.clientRect = clientRect
      this.renderPopup()
    },
    /**
     * 閉じるイベント
     */
    $_onClose() {
      // 非表示にする
      this.$_hide()
    },
    /**
     * Popup を Hide
     */
    $_hide() {
      this.popupData = null
      this.members = []
      this.placeCount = 0
      this.renderPopup()
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.popup-box {
  min-width: 180px;
  width: auto;
  position: absolute;
  // interleaved: false によって UI が deck.gl の canvas （z-index: 999）より背面に配置される問題への対応。
  z-index: 1000;

  ::v-deep .loading-spin {
    $-spin-size: 24px;
    width: $-spin-size;
    height: $-spin-size;
    top: calc(50% - #{$-spin-size} / 2);
    left: calc(50% - #{$_spin-size} / 2);
    margin: 0;
    border-width: 4px;
  }
}

.footer {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 4px;
  margin-top: 4px;
}

.link {
  color: $text-color-link;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}

.body {
  padding: 2px 8px;
  row-gap: 2px;
  column-gap: 8px;
  display: grid;
  grid-template-columns: 16px max-content 1fr;
}

.item-icon {
  display: flex;
  justify-content: center;
  align-items: center;

  &.item-icon-user {
    grid-row: 1;
    grid-column: 1;
  }

  &.item-icon-place {
    grid-row: 2;
    grid-column: 1;
  }
}

.item-text {
  text-align: right;
  font-size: 0.75rem;
  color: #555555;
  vertical-align: middle;

  &.item-text-user {
    grid-row: 1;
    grid-column: 2;
  }

  &.item-text-place {
    grid-row: 2;
    grid-column: 2;
  }
}

.svg-icon {
  width: 12px;
  height: 12px;
  fill: #555555;
}
</style>
